import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TabNavigation from '../../components/TabNavigation';
import { Scale, ExternalLink, ArrowLeft, Info, Plus, Pencil, Trash2, Loader2, X, Check,
  ChevronDown, ChevronRight, ListChecks } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';

/**
 * Legal References — tenant-customizable list of safety legal/compliance
 * references. Read-only for most users; Admin / HSE Manager / Safety Manager
 * can add, edit, and delete entries.
 *
 * Backend:
 *   GET    /api/legal-references         -> { references, can_manage }
 *   POST   /api/legal-references         { category, title, citation, description, url }
 *   PUT    /api/legal-references/{id}     { ...same }
 *   DELETE /api/legal-references/{id}
 *
 * Each reference that came from the catalog also knows what it REQUIRES: the
 * obligations the statute is the legal basis for, filtered to this site. That is
 * the question a legal register otherwise leaves hanging — the library says you
 * are subject to the Factories Act, so what does it actually make you do, and are
 * you tracking any of it? See ReferenceRequirements below.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api/legal-references`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const EMPTY = { category: '', title: '', citation: '', description: '', url: '' };

/**
 * `embedded` renders just the library body, for use as the References tab of
 * the merged Legal & Compliance module. The standalone page chrome (nav bar,
 * back link, page title) is dropped there because the host page already
 * provides it. Kept as one component rather than split into a page plus a
 * panel so there is a single place where the library CRUD lives.
 */
const COMPLIANCE_API = `${process.env.REACT_APP_BACKEND_URL}/api/compliance`;

const SEVERITY_CHIP = {
  critical: 'bg-rose-600 text-white',
  high: 'bg-amber-500 text-white',
  medium: 'bg-slate-400 text-white',
};

/**
 * The requirements one legal reference is the basis for, for the active site.
 *
 * Loaded on expand rather than with the list: a library of twenty references
 * would otherwise fire twenty requests to answer a question nobody has asked yet.
 *
 * Adding uses the ordinary compliance adopt endpoint, so a requirement added here
 * and one added from "What applies to us" are the same row created the same way —
 * and adding the same one twice from two screens still adds nothing.
 */
const ReferenceRequirements = ({ reference, siteId, canManage, onAdopted }) => {
  const { showError, showSuccess } = useAlert();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!siteId || !reference.catalog_id) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${COMPLIANCE_API}/catalog/for-reference`, {
        params: { reference_id: reference.catalog_id, site_id: siteId },
        ...authHeaders(),
      });
      setData(res);
    } catch (e) {
      showError(e?.response?.data?.detail
        || 'Could not load the requirements for this reference.');
    } finally {
      setLoading(false);
    }
  }, [siteId, reference.catalog_id, showError]);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  // A reference the tenant typed in by hand has no catalog entry, so nothing to
  // derive requirements from — say nothing rather than show an empty expander.
  if (!reference.catalog_id) return null;

  const addSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setSaving(true);
    try {
      const { data: res } = await axios.post(`${COMPLIANCE_API}/catalog/adopt`,
        { site_id: siteId, catalog_ids: ids }, authHeaders());
      const made = res.created?.length || 0;
      showSuccess(made
        ? `${made} requirement${made === 1 ? '' : 's'} added to the register, `
          + `${res.events_created} due period${res.events_created === 1 ? '' : 's'} scheduled.`
        : 'Already in the register — nothing added.');
      setSelected(new Set());
      setData(null);          // re-read so the "in register" marks are current
      await load();
      onAdopted?.();
    } catch (e) {
      showError(e?.response?.data?.detail || 'Could not add to the register.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <button onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-[#1B5C94]
                         hover:underline">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <ListChecks className="w-4 h-4" />
        What this requires us to do
        {data && (
          <span className="text-xs font-normal text-gray-500">
            {data.entries.length} in the library
            {data.applicable > 0 && ` · ${data.applicable} typical for your industry`}
            {data.untracked > 0 && ` · ${data.untracked} not in the register`}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading the library…
            </div>
          )}

          {!loading && data && data.entries.length === 0 && (
            <p className="text-sm text-gray-500 py-2">
              No requirements in the library cite this reference yet.
            </p>
          )}

          {!loading && data && data.entries.length > 0 && (
            <>
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {data.entries.map((e) => (
                  <li key={e.catalog_id}
                      className={`flex items-start gap-3 px-3 py-2.5
                                  ${e.industry_match ? '' : 'bg-gray-50/50'}`}>
                    {/* Selectable whatever the industry says. The highlight is a
                        hint; the plant knows what it has, and a duty that is
                        untypical for the industry is still a duty. */}
                    <input type="checkbox" className="mt-1"
                           disabled={e.adopted || !canManage}
                           checked={selected.has(e.catalog_id)}
                           onChange={() => toggle(e.catalog_id)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold
                                          ${SEVERITY_CHIP[e.severity] || SEVERITY_CHIP.medium}`}>
                          {e.severity}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{e.title}</span>
                        {e.adopted && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100
                                           text-emerald-800 text-[10px] font-medium">
                            In register
                          </span>
                        )}
                        {e.baseline ? (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600
                                           text-[10px] font-medium">
                            Every site
                          </span>
                        ) : e.industry_match ? (
                          <span className="px-1.5 py-0.5 rounded bg-[#1B5C94]/10
                                           text-[#1B5C94] text-[10px] font-medium">
                            Typical for your industry
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">{e.citation}</div>
                      <div className="text-xs text-gray-500 mt-1">{e.consequence}</div>
                      <div className="text-[11px] text-gray-400 mt-1">
                        {e.frequency.replace(/_/g, ' ')}
                        {e.reasons?.length > 0 && ` · ${e.reasons.join(' · ')}`}
                      </div>
                      {e.state_note && (
                        <div className="text-[11px] text-amber-700 mt-1">{e.state_note}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {canManage && (
                <div className="mt-3 flex items-center gap-3">
                  <Button onClick={addSelected} disabled={saving || selected.size === 0}
                          className="bg-[#1B5C94] hover:bg-[#164a78]">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            : <Plus className="w-4 h-4 mr-2" />}
                    Add {selected.size || ''} to register
                  </Button>
                  {/* Selects the HIGHLIGHTED untracked entries, not every one:
                      bulk-adopting the whole library would put requirements in a
                      plant's register that nobody judged applied to it. */}
                  <button
                    onClick={() => setSelected(new Set(
                      data.entries.filter((e) => e.industry_match && !e.adopted)
                        .map((e) => e.catalog_id)))}
                    className="text-xs font-medium text-[#1B5C94] hover:underline">
                    Select those typical for your industry
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const LegalReferences = ({ user, onLogout, embedded = false, onRegisterChanged }) => {
  // Requirements are per site — the same reference proposes different duties to a
  // plant with a boiler and one without.
  const { activeSiteId } = useSite();
  const navigate = useNavigate();
  const { confirm, showError } = useAlert();
  const [loading, setLoading] = useState(true);
  const [refs, setRefs] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // ref being edited, or null for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API, authHeaders());
      setRefs(res.data.references || []);
      setCanManage(!!res.data.can_manage);
    } catch (e) {
      setError('Failed to load legal references.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group flat list by category, preserving server order.
  const groups = useMemo(() => {
    const order = [];
    const map = new Map();
    refs.forEach((r) => {
      const cat = r.category || 'Other';
      if (!map.has(cat)) { map.set(cat, []); order.push(cat); }
      map.get(cat).push(r);
    });
    return order.map((cat) => ({ category: cat, references: map.get(cat) }));
  }, [refs]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormError(''); setShowForm(true); };
  const openEdit = (ref) => {
    setEditing(ref);
    setForm({
      category: ref.category || '', title: ref.title || '', citation: ref.citation || '',
      description: ref.description || '', url: ref.url || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!form.title.trim()) { setFormError('Title is required.'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editing) {
        await axios.put(`${API}/${editing.id}`, form, authHeaders());
      } else {
        await axios.post(API, form, authHeaders());
      }
      setShowForm(false);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ref) => {
    const confirmed = await confirm(
      'Delete legal reference',
      `Delete "${ref.title}"? This cannot be undone.`,
      { destructive: true, confirmLabel: 'Delete' }
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/${ref.id}`, authHeaders());
      await load();
    } catch (e) {
      showError(e?.response?.data?.detail || 'Failed to delete legal reference.');
    }
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {!embedded && <TabNavigation user={user} onLogout={onLogout} />}

      <div className={embedded ? '' : 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {!embedded && (
        <button
          onClick={() => navigate('/safety')}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Safety Management
        </button>
        )}

        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            {!embedded && (
            <>
            <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <Scale className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Legal References</h1>
              <p className="text-gray-600 mt-1">Safety standards and regulations relevant to this module</p>
            </div>
            </>
            )}
          </div>
          {canManage && (
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 shrink-0">
              <Plus className="w-4 h-4 mr-2" /> Add Reference
            </Button>
          )}
        </div>

        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
          <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            This list is maintained by your organisation and does not constitute legal advice.
            Applicability varies by jurisdiction and operation — confirm the regulations that apply
            to your organisation with a qualified adviser.
          </p>
        </div>

        {error && <div className="text-sm text-red-600 mb-4">{error}</div>}

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : refs.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            No legal references yet.{canManage ? ' Use “Add Reference” to create one.' : ''}
          </div>
        ) : (
          <div className="space-y-8">
            {groups.map((group) => (
              <section key={group.category}>
                <h2 className="text-lg font-bold text-gray-800 mb-3">{group.category}</h2>
                <div className="space-y-3">
                  {group.references.map((ref) => (
                    <div key={ref.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="text-base font-semibold text-gray-900">{ref.title}</h3>
                          {ref.citation && (
                            <span className="inline-block mt-1 text-xs font-bold text-slate-600 bg-slate-100 rounded px-2 py-0.5">
                              {ref.citation}
                            </span>
                          )}
                          {ref.description && <p className="text-sm text-gray-600 mt-2">{ref.description}</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {ref.url && (
                            <a href={ref.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm font-medium text-[#1B5C94] hover:underline">
                              Source <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {canManage && (
                            <>
                              <button onClick={() => openEdit(ref)} className="text-gray-400 hover:text-blue-600 p-1" title="Edit">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => remove(ref)} className="text-gray-400 hover:text-red-600 p-1" title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <ReferenceRequirements
                        reference={ref}
                        siteId={activeSiteId}
                        canManage={canManage}
                        onAdopted={onRegisterChanged}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Edit' : 'Add'} Legal Reference</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {[
                { k: 'title', label: 'Title *', placeholder: 'e.g. The Factories Act, 1948' },
                { k: 'citation', label: 'Citation / clause', placeholder: 'e.g. Act No. 63 of 1948' },
                { k: 'category', label: 'Category', placeholder: 'e.g. India' },
                { k: 'url', label: 'Source URL', placeholder: 'https://…' },
              ].map(({ k, label, placeholder }) => (
                <div key={k}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    value={form[k]}
                    onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              {formError && <div className="text-sm text-red-600">{formError}</div>}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={saveForm} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalReferences;
